import useDocumentRequests from '@/layouts/DashboardLayout/SubLayouts/BuildingBlocks/useDocumentRequests';
import useFormRequest from '@/layouts/DashboardLayout/SubLayouts/BuildingBlocks/useFormRequests';
import useInvoiceRequests from '@/layouts/DashboardLayout/SubLayouts/BuildingBlocks/useInvoiceRequests';
import useTermsTemplates from '@/layouts/DashboardLayout/SubLayouts/BuildingBlocks/useTermsTemplates';
import { ResourceType } from '@/layouts/DashboardLayout/SubLayouts/BuildingBlocks/common';
import Search from '@/components/DesignLibrary/Atoms/Search';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import Header from '@/components/DesignLibrary/Header';
import TeamsContext from '@/providers/Teams/TeamsContext';
import ListCell from '@/components/DesignLibrary/ListCell';
import CreateEditDocumentRequestModal from '@/modals/CreateEditDocumentRequestModal';
import FormRequestModal from '@/modals/FormRequestModal';
import InvoiceRequestModal from '@/modals/InvoiceRequestModal';
import ProductEditModal from '@/modals/ProductEditModal';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import CreateEditTermsTemplateModal from '@/modals/CreateEditTermsTemplateModal';
import useHighlightSearchTerm from '@hooks/useHighlightSearchTerm';
import { Switch } from '@/components/DesignLibrary/Atoms';
import * as ApiModels from '@typings/api-models';
import Plus from '@/components/Icons/Plus';

import s from './WorkflowDetails.module.scss';

const BuildingBlocks: FC<{
  refetchWorkflow: () => Promise<void>;
  usedBlocks: ApiModels.CommonWorkflowResponse['buildingBlocks'],
}> = ({ refetchWorkflow, usedBlocks }) => {
  const { creatorId } = useContext(TeamsContext);

  const [search, setSearch] = useState('');
  const [resourceType, setResourceType] = useState<ResourceType | null>('custom-terms');
  const [bookingsSelected, setBookingsSelected] = useState(false);
  const [onlyUsedOnes, setOnlyUsedOnes] = useState(true);

  const [termsRequestId, setTermsRequestId] = useState<string | null>(null);
  const [documentRequestId, setDocumentRequestId] = useState<string | null>(null);
  const [formRequestId, setFormRequestId] = useState<string | null>(null);
  const [invoiceRequestId, setInvoiceRequestId] = useState<string | null>(null);
  const [bookingPriceId, setBookingPriceId] = useState<string | null>(null);

  const [openTermsModal, setOpenTermsModal] = useState(false);
  const [openDocumentModal, setOpenDocumentModal] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);

  const listRef = useRef<HTMLDivElement | null>(null);

  useHighlightSearchTerm(search, [listRef.current as HTMLElement]);

  const documentRequestData = useDocumentRequests({
    creatorId: creatorId || undefined,
    perPage: 1000,
    resourceType: resourceType || 'document-requests',
    search,
  });

  const formRequestData = useFormRequest({
    creatorId: creatorId || undefined,
    perPage: 1000,
    resourceType: resourceType || 'form-requests',
    search,
  });

  const invoiceRequestData = useInvoiceRequests({
    creatorId: creatorId || undefined,
    perPage: 1000,
    resourceType: resourceType || 'invoices',
    search,
  });

  const termsTemplatesData = useTermsTemplates({
    creatorId: creatorId || undefined,
    perPage: 1000,
    resourceType: resourceType || 'custom-terms',
    search,
  });

  const {
    data: invoiceRequest,
    fetchingState: { isFetching: invoiceRequestFetching },
    getData: getInvoiceRequestTemplateById,
  } = useNoodleApi(tsClient.invoices.getInvoiceRequestTemplateById);
  const {
    data: documentRequest,
    fetchingState: { isFetching: documentRequestFetching },
    getData: getDocumentRequestById,
  } = useNoodleApi(tsClient.documentRequests.getDocumentRequestById);
  const {
    data: formRequest,
    fetchingState: { isFetching: formRequestFetching },
    getData: getFormRequestById,
  } = useNoodleApi(tsClient.formRequests.getFormRequestTemplateById);
  const {
    data: termsRequest,
    fetchingState: { isFetching: termsRequestFetching },
    getData: getTermsRequestById,
  } = useNoodleApi(tsClient.customTerms.getCustomTermsTemplateById);
  const {
    data: bookingPrice,
    fetchingState: { isFetching: isFetchingBookingPrice },
    getData: getBookingPrice,
  } = useNoodleApi(tsClient.getPrice);

  const handleSearch = (newSearch: string): void => {
    setSearch(newSearch ? newSearch.toLowerCase() : '');
  };

  const documentRequestsFiltered = documentRequestData.items.filter(
    ({ id }) => onlyUsedOnes ? usedBlocks.documentRequests.some(block => block.id === id) : true,
  );

  const formRequestsFiltered = formRequestData.items.filter(
    ({ id }) => onlyUsedOnes ? usedBlocks.formRequests.some(block => block.id === id) : true,
  );

  const invoicesFiltered = invoiceRequestData.items.filter(
    ({ id }) => onlyUsedOnes ? usedBlocks.invoices.some(block => block.id === id) : true,
  );

  const customTermsFiltered = termsTemplatesData.items.filter(
    ({ id }) => onlyUsedOnes ? usedBlocks.customTerms.some(block => block.id === id) : true,
  );

  const selectedData = (resourceType === 'document-requests' && documentRequestsFiltered)
    || (resourceType === 'form-requests' && formRequestsFiltered)
    || (resourceType === 'invoices' && invoicesFiltered)
    || (resourceType === 'custom-terms' && customTermsFiltered) || [];

  useEffect(() => {
    if (resourceType) {
      setBookingsSelected(false);
    }
  }, [resourceType]);

  useEffect(() => {
    if (bookingsSelected) {
      setResourceType(null);
    }
  }, [bookingsSelected]);

  useEffect(() => {
    if (termsRequestId) {
      getTermsRequestById({ id: termsRequestId });
    }
  }, [getTermsRequestById, termsRequestId, openTermsModal]);

  useEffect(() => {
    if (documentRequestId) {
      getDocumentRequestById({ id: documentRequestId });
    }
  }, [documentRequestId, getDocumentRequestById, openDocumentModal]);

  useEffect(() => {
    if (formRequestId) {
      getFormRequestById({ id: formRequestId });
    }
  }, [formRequestId, getFormRequestById, openFormModal]);

  useEffect(() => {
    if (invoiceRequestId) {
      getInvoiceRequestTemplateById({ id: invoiceRequestId });
    }
  }, [invoiceRequestId, getInvoiceRequestTemplateById, openInvoiceModal]);

  useEffect(() => {
    if (bookingPriceId) {
      getBookingPrice({ priceId: bookingPriceId });
    }
  }, [bookingPriceId, getBookingPrice]);

  return (
    <div style={{ maxHeight: '100vh', overflowY: 'auto' }}>
      <Header
        hierarchy="h2"
        title="Building Blocks"
        tabs={[
          {
            label: `Custom Terms`,
            onClick: () => setResourceType('custom-terms'),
            selected: resourceType === 'custom-terms',
          },
          {
            label: 'Document Requests',
            onClick: () => setResourceType('document-requests'),
            selected: resourceType === 'document-requests',
          },
          {
            label: 'Form Requests',
            onClick: () => setResourceType('form-requests'),
            selected: resourceType === 'form-requests',
          },
          {
            label: 'Invoice Requests',
            onClick: () => setResourceType('invoices'),
            selected: resourceType === 'invoices',
          },
          {
            label: 'Bookings',
            onClick: () => { setBookingsSelected(true); },
            selected: bookingsSelected,
          },
        ]}
        actions={bookingsSelected
          ? []
          : [
            {
              icon: Plus,
              label: `Create ${resourceType === 'document-requests' ? ' Document Request' : ''}
              ${resourceType === 'form-requests' ? ' Form Request' : ''}
              ${resourceType === 'invoices' ? ' Invoice Request' : ''}
              ${resourceType === 'custom-terms' ? ' Custom Terms' : ''}`,
              onClick:() => {
                switch (resourceType) {
                case 'document-requests':
                  setDocumentRequestId(null);
                  setOpenDocumentModal(true);
                  break;
                case 'form-requests':
                  setFormRequestId(null);
                  setOpenFormModal(true);
                  break;
                case 'invoices':
                  setInvoiceRequestId(null);
                  setOpenInvoiceModal(true);
                  break;
                case 'custom-terms':
                  setTermsRequestId(null);
                  setOpenTermsModal(true);
                  break;
                default:
                  break;
                }
              },
            },
          ]}
        isSticky
      />
      {!bookingsSelected && <>
        <Search onChange={handleSearch} />
        <Switch
          name="used"
          label="Show only the ones used in this workflow template"
          isChecked={onlyUsedOnes}
          onChange={() => setOnlyUsedOnes(prev => !prev)}
          className={s.used}
        />
      </>}
      <div ref={listRef}>
        {selectedData
          .map(item => (
            <ListCell
              {...item}
              key={item.id}
              href={undefined}
              onClick={() => {
                switch (resourceType) {
                case 'document-requests':
                  setDocumentRequestId(item.id);
                  setOpenDocumentModal(true);
                  break;
                case 'form-requests':
                  setFormRequestId(item.id);
                  setOpenFormModal(true);
                  break;
                case 'invoices':
                  setInvoiceRequestId(item.id);
                  setOpenInvoiceModal(true);
                  break;
                case 'custom-terms':
                  setTermsRequestId(item.id);
                  setOpenTermsModal(true);
                  break;
                default:
                  break;
                }
              }}
              isRouting={
                (documentRequestFetching && item.id === documentRequestId)
                || (formRequestFetching && item.id === formRequestId)
                || (termsRequestFetching && item.id === termsRequestId)
                || (invoiceRequestFetching && item.id === invoiceRequestId)
                || (isFetchingBookingPrice && item.id === bookingPriceId)}
            />
          ))}
      </div>
      <div className={s.noItems}>
        {selectedData.length === 0 && (
          <p className='body-sm'>
            {resourceType === 'document-requests' && `No document requests ${onlyUsedOnes ? 'used' : ''}`}
            {resourceType === 'form-requests' && `No form requests ${onlyUsedOnes ? 'used' : ''}`}
            {resourceType === 'invoices' && `No invoices ${onlyUsedOnes ? 'used' : ''}`}
            {resourceType === 'custom-terms' && `No custom terms ${onlyUsedOnes ? 'used' : ''}`}
            {bookingsSelected && `No bookings used`}
          </p>
        )}
      </div>
      {openTermsModal && (termsRequest || !termsRequestId) && (
        <CreateEditTermsTemplateModal
          termsTemplate={termsRequestId ? termsRequest : null}
          onClose={() => { setTermsRequestId(null); setOpenTermsModal(false); }}
          onSubmit={async () => {
            await refetchWorkflow();
            await termsTemplatesData.reloadList();
            setTermsRequestId(null);
            setOpenTermsModal(false);
          }}
        />
      )}
      {openDocumentModal && (documentRequest || !documentRequestId) && (
        <CreateEditDocumentRequestModal
          documentRequest={documentRequestId ? documentRequest : null}
          onClose={() => { setDocumentRequestId(null); setOpenDocumentModal(false); }}
          onSubmit={async () => {
            await refetchWorkflow();
            await documentRequestData.reloadList();
            setDocumentRequestId(null);
            setOpenDocumentModal(false);
          }}
        />
      )}
      {openFormModal && (formRequest || !formRequestId) && creatorId && (
        <FormRequestModal
          beforeClose={async () => {
            await refetchWorkflow();
            await formRequestData.reloadList();
            setFormRequestId(null);
            setOpenFormModal(false);
          }}
          onClose={() => { setFormRequestId(null); setOpenFormModal(false); }}
          formRequestTemplate={formRequestId ? formRequest : null}
          creatorId={creatorId}
        />
      )}
      {openInvoiceModal && (invoiceRequest || !invoiceRequestId) && (
        <InvoiceRequestModal
          beforeClose={async () => {
            await refetchWorkflow();
            await invoiceRequestData.reloadList();
            setInvoiceRequestId(null);
            setOpenInvoiceModal(false);
          }}
          onClose={() => { setInvoiceRequestId(null); setOpenInvoiceModal(false); }}
          invoiceTemplate={invoiceRequestId ? invoiceRequest : null}
        />
      )}
      {bookingPrice?.product?.slug && bookingPrice?.product?.creator?.slug && bookingPriceId && (
        <ProductEditModal
          onClose={async () => {
            setBookingPriceId(null);
            await refetchWorkflow();
          }}
          creatorSlug={bookingPrice.product.creator.slug}
          productSlug={bookingPrice.product.slug}
        />
      )}
    </div>
  );
};

export default BuildingBlocks;
